import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Plus, Trash2, Upload, Images, Layers, Edit2, Save, X,
  Palette, ExternalLink, FolderPlus, GripVertical, Globe, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ThemePicker } from '@/components/gallery/ThemePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const THEME_LIST = [
  'grid', 'masonry', 'polaroid', 'slideshow', 'kenburns', 'carousel',
  'mosaic', 'justified', 'timeline', 'filmstrip', 'spotlight',
  'scrapbook', 'cube3d', 'coverflow', 'flipcards', 'marquee',
  'kinetic', 'parallax', 'immersive',
];

export default function AdminGalleryCreator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromParam = new URLSearchParams(location.search).get('from');
  const backTo = fromParam ? decodeURIComponent(fromParam) : '/admin/dashboard';

  const [galleries, setGalleries] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [media, setMedia] = useState([]);
  const [activeGalleryId, setActiveGalleryId] = useState(null);
  const [activeAlbumId, setActiveAlbumId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newGallerySlug, setNewGallerySlug] = useState('');
  const [newGalleryTitle, setNewGalleryTitle] = useState('');
  const [newAlbumTitle, setNewAlbumTitle] = useState('');
  const [importWpUrl, setImportWpUrl] = useState('');
  const [wpUser, setWpUser] = useState('');
  const [wpPass, setWpPass] = useState('');
  const [showWpAuth, setShowWpAuth] = useState(false);
  const [uploading, setUploading] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    if (user && !['super_admin', 'admin'].includes(user.role)) {
      toast.error('Admins only');
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    loadGalleries();
  }, []);

  useEffect(() => {
    if (activeGalleryId) loadAlbums(activeGalleryId);
    else setAlbums([]);
  }, [activeGalleryId]);

  useEffect(() => {
    if (activeAlbumId) loadMedia(activeAlbumId);
    else setMedia([]);
  }, [activeAlbumId]);

  const loadGalleries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gc/admin/galleries`, { headers: authHeaders() });
      setGalleries(res.data.items || []);
      if (!activeGalleryId && res.data.items?.length) {
        setActiveGalleryId(res.data.items[0].id);
      }
    } catch (e) {
      toast.error('Failed to load galleries');
    } finally {
      setLoading(false);
    }
  };

  const loadAlbums = async (galleryId) => {
    try {
      const res = await axios.get(`${API}/gc/admin/albums`, { params: { gallery_id: galleryId }, headers: authHeaders() });
      const items = res.data.items || [];
      setAlbums(items);
      // Keep sidebar counts in sync without a full gallery list refetch.
      const totalMedia = items.reduce((s, a) => s + (a.media_count || 0), 0);
      setGalleries((prev) =>
        prev.map((g) => (g.id === galleryId ? { ...g, album_count: items.length, media_count: totalMedia } : g))
      );
      if (items.length) setActiveAlbumId(items[0].id);
      else setActiveAlbumId(null);
    } catch (e) {
      toast.error('Failed to load albums');
    }
  };

  const loadMedia = async (albumId) => {
    try {
      const res = await axios.get(`${API}/gc/admin/media`, { params: { album_id: albumId }, headers: authHeaders() });
      const items = res.data.items || [];
      setMedia(items);
      // Patch the album's media_count in state so pill + sidebar reflect reality instantly.
      setAlbums((prev) => {
        const next = prev.map((a) => (a.id === albumId ? { ...a, media_count: items.length } : a));
        if (activeGalleryId) {
          const totalMedia = next.reduce((s, a) => s + (a.media_count || 0), 0);
          setGalleries((pg) =>
            pg.map((g) => (g.id === activeGalleryId ? { ...g, media_count: totalMedia } : g))
          );
        }
        return next;
      });
    } catch (e) {
      toast.error('Failed to load media');
    }
  };

  const currentGallery = galleries.find((g) => g.id === activeGalleryId);
  const currentAlbum = albums.find((a) => a.id === activeAlbumId);

  // Create gallery ----------------------------------------------------
  const createGallery = async () => {
    const slug = newGallerySlug.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-');
    const title = newGalleryTitle.trim();
    if (!slug || !title) return toast.error('Slug and title required');
    try {
      await axios.post(`${API}/gc/admin/galleries`, { slug, title, theme: 'grid' }, { headers: authHeaders() });
      setNewGallerySlug('');
      setNewGalleryTitle('');
      toast.success('Gallery created');
      loadGalleries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  };

  const updateGallery = async (patch) => {
    if (!currentGallery) return;
    try {
      await axios.put(`${API}/gc/admin/galleries/${currentGallery.id}`, patch, { headers: authHeaders() });
      setGalleries((prev) => prev.map((g) => (g.id === currentGallery.id ? { ...g, ...patch } : g)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const deleteGallery = async () => {
    if (!currentGallery) return;
    if (!window.confirm(`Delete gallery "${currentGallery.title}" and ALL its albums and media? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/gc/admin/galleries/${currentGallery.id}`, { headers: authHeaders() });
      toast.success('Deleted');
      setActiveGalleryId(null);
      setActiveAlbumId(null);
      loadGalleries();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // Create album ------------------------------------------------------
  const createAlbum = async () => {
    if (!currentGallery) return toast.error('Pick a gallery first');
    const title = newAlbumTitle.trim();
    if (!title) return toast.error('Album title required');
    try {
      const res = await axios.post(
        `${API}/gc/admin/albums`,
        { gallery_id: currentGallery.id, title, sort_order: albums.length },
        { headers: authHeaders() }
      );
      setNewAlbumTitle('');
      toast.success('Album created');
      loadAlbums(currentGallery.id);
      setActiveAlbumId(res.data.album?.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  };

  const updateAlbum = async (patch) => {
    if (!currentAlbum) return;
    try {
      await axios.put(`${API}/gc/admin/albums/${currentAlbum.id}`, patch, { headers: authHeaders() });
      setAlbums((prev) => prev.map((a) => (a.id === currentAlbum.id ? { ...a, ...patch } : a)));
    } catch (err) {
      toast.error('Save failed');
    }
  };

  const deleteAlbum = async () => {
    if (!currentAlbum) return;
    if (!window.confirm(`Delete album "${currentAlbum.title}"? All media inside will be removed.`)) return;
    try {
      await axios.delete(`${API}/gc/admin/albums/${currentAlbum.id}`, { headers: authHeaders() });
      toast.success('Deleted');
      loadAlbums(currentGallery.id);
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // Upload media ------------------------------------------------------
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!currentAlbum || !files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/gc/admin/media/upload?album_id=${currentAlbum.id}`, fd, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(`${files.length} file(s) uploaded`);
      loadMedia(currentAlbum.id);
      if (currentGallery) loadAlbums(currentGallery.id);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const updateMedia = async (mediaId, patch) => {
    try {
      await axios.put(`${API}/gc/admin/media/${mediaId}`, patch, { headers: authHeaders() });
      setMedia((prev) => prev.map((m) => (m.id === mediaId ? { ...m, ...patch } : m)));
    } catch (err) {
      toast.error('Save failed');
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/gc/admin/media/${mediaId}`, { headers: authHeaders() });
      setMedia((prev) => prev.filter((m) => m.id !== mediaId));
      // Decrement the counts in sidebar + album pill immediately.
      if (currentAlbum) {
        setAlbums((prev) => {
          const next = prev.map((a) =>
            a.id === currentAlbum.id ? { ...a, media_count: Math.max(0, (a.media_count || 0) - 1) } : a
          );
          if (activeGalleryId) {
            const totalMedia = next.reduce((s, a) => s + (a.media_count || 0), 0);
            setGalleries((pg) =>
              pg.map((g) => (g.id === activeGalleryId ? { ...g, media_count: totalMedia } : g))
            );
          }
          return next;
        });
      }
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !currentAlbum) return;
    const oldIndex = media.findIndex((m) => m.id === active.id);
    const newIndex = media.findIndex((m) => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(media, oldIndex, newIndex);
    setMedia(next);
    try {
      await axios.post(
        `${API}/gc/admin/albums/${currentAlbum.id}/reorder`,
        { ids: next.map((m) => m.id) },
        { headers: authHeaders() }
      );
    } catch (err) {
      toast.error('Reorder failed');
    }
  };

  const importFromWordPress = async () => {
    if (!currentGallery) return toast.error('Pick a gallery first');
    const site = importWpUrl.trim();
    if (!site) return toast.error('Enter the WordPress site URL');
    try {
      const res = await axios.post(
        `${API}/gc/admin/import/wordpress`,
        {
          gallery_id: currentGallery.id,
          wp_site: site,
          per_page: 50,
          wp_username: wpUser.trim() || null,
          wp_app_password: wpPass.trim() || null,
        },
        { headers: authHeaders() }
      );
      toast.success(`Imported ${res.data.imported} items`);
      setImportWpUrl('');
      setWpUser('');
      setWpPass('');
      setShowWpAuth(false);
      loadAlbums(currentGallery.id);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Import failed';
      toast.error(msg);
      if (err.response?.status === 401) setShowWpAuth(true);
    }
  };

  // Upload from computer — auto-creates a default album if one doesn't exist yet.
  const handleDirectUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!currentGallery || !files.length) return;
    setUploading(true);
    try {
      let albumId = activeAlbumId;
      if (!albumId) {
        const res = await axios.post(
          `${API}/gc/admin/albums`,
          { gallery_id: currentGallery.id, title: 'Album 1', sort_order: 0 },
          { headers: authHeaders() }
        );
        albumId = res.data.album?.id;
      }
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/gc/admin/media/upload?album_id=${albumId}`, fd, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(`${files.length} file(s) uploaded`);
      await loadAlbums(currentGallery.id);
      setActiveAlbumId(albumId);
      loadMedia(albumId);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const embedCode = currentGallery
    ? `<iframe src="${window.location.origin}/g/${currentGallery.slug}" width="100%" height="800" frameborder="0"></iframe>`
    : '';

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="admin-gallery-creator">
      {/* Header */}
      <div className="border-b border-stone-800 bg-stone-900/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Link to={backTo} className="inline-flex items-center gap-2 text-base text-muted-foreground hover:text-primary">
              <ArrowLeft className="w-5 h-5" /> Back
            </Link>
            <div className="flex items-center gap-3">
              <Images className="w-8 h-8 text-primary" />
              <h1 className="text-2xl font-bold">Gallery Creator</h1>
            </div>
          </div>
          {currentGallery && (
            <Link
              to={`/g/${currentGallery.slug}`}
              target="_blank"
              className="inline-flex items-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg text-base font-bold"
            >
              <ExternalLink className="w-5 h-5" /> View live
            </Link>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Sidebar: Galleries */}
        <aside className="space-y-4">
          <div>
            <h2 className="text-base uppercase tracking-widest text-stone-300 font-bold mb-3">Your Galleries</h2>
            <div className="space-y-2 mb-4">
              {galleries.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setActiveGalleryId(g.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg text-base ${
                    activeGalleryId === g.id ? 'bg-primary text-primary-foreground' : 'bg-stone-900 hover:bg-stone-800 text-stone-200'
                  }`}
                  data-testid={`gc-gallery-${g.slug}`}
                >
                  <div className="font-bold flex items-center gap-2">
                    {g.is_public ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                    {g.title}
                  </div>
                  <div className="text-sm opacity-80 mt-0.5">
                    {g.album_count || 0} album{g.album_count === 1 ? '' : 's'} · {g.media_count || 0} image{g.media_count === 1 ? '' : 's'}
                  </div>
                </button>
              ))}
              {galleries.length === 0 && (
                <div className="text-sm text-stone-500 italic px-2">
                  No galleries yet — create your first one below.
                </div>
              )}
            </div>
            <div className="space-y-3 bg-stone-900 border border-stone-800 rounded-xl p-4">
              <h3 className="text-sm font-bold uppercase tracking-widest text-primary">Create New Gallery</h3>
              <Field label="Gallery Name">
                <input
                  value={newGalleryTitle}
                  onChange={(e) => {
                    setNewGalleryTitle(e.target.value);
                    if (!newGallerySlug) {
                      setNewGallerySlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
                    }
                  }}
                  placeholder="e.g. Carnival Photos"
                  className={inputCls}
                  data-testid="gc-new-gallery-title"
                />
              </Field>
              <Field label="URL (auto-filled)">
                <input
                  value={newGallerySlug}
                  onChange={(e) => setNewGallerySlug(e.target.value)}
                  placeholder="carnival-photos"
                  className={inputCls}
                  data-testid="gc-new-gallery-slug"
                />
              </Field>
              <button
                onClick={createGallery}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg text-base font-bold hover:bg-primary/90"
                data-testid="gc-create-gallery"
              >
                <Plus className="w-5 h-5" /> Create Gallery
              </button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="space-y-6">
          {!currentGallery ? (
            <div className="text-stone-400">Pick or create a gallery to begin.</div>
          ) : (
            <>
              {/* Gallery settings */}
              <section className="border border-stone-800 rounded-xl p-5 bg-stone-900/40">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <h3 className="text-lg font-bold uppercase tracking-widest text-stone-200">Gallery Settings</h3>
                    <div className="flex items-center gap-2 text-sm text-stone-300 bg-stone-900 border border-stone-700 rounded-full px-3 py-1">
                      <Images className="w-4 h-4 text-primary" />
                      <span>
                        <span className="font-bold text-stone-100">{albums.reduce((s, a) => s + (a.media_count || 0), 0)}</span> image{albums.reduce((s, a) => s + (a.media_count || 0), 0) === 1 ? '' : 's'}
                        <span className="opacity-60"> across {albums.length} album{albums.length === 1 ? '' : 's'}</span>
                      </span>
                    </div>
                  </div>
                  <button onClick={deleteGallery} className="text-red-300 hover:text-red-400 text-base inline-flex items-center gap-2">
                    <Trash2 className="w-5 h-5" /> Delete gallery
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Title">
                    <input className={inputCls} value={currentGallery.title}
                      onChange={(e) => setGalleries((prev) => prev.map((g) => g.id === currentGallery.id ? { ...g, title: e.target.value } : g))}
                      onBlur={(e) => updateGallery({ title: e.target.value })}
                    />
                  </Field>
                  <Field label="Columns">
                    <input type="number" className={inputCls} value={currentGallery.columns || 4}
                      onChange={(e) => setGalleries((prev) => prev.map((g) => g.id === currentGallery.id ? { ...g, columns: Number(e.target.value) } : g))}
                      onBlur={(e) => updateGallery({ columns: Number(e.target.value) || 4 })}
                    />
                  </Field>
                  <Field label="Gap (px)">
                    <input type="number" className={inputCls} value={currentGallery.gap_px || 12}
                      onBlur={(e) => updateGallery({ gap_px: Number(e.target.value) || 12 })}
                      onChange={(e) => setGalleries((prev) => prev.map((g) => g.id === currentGallery.id ? { ...g, gap_px: Number(e.target.value) } : g))}
                    />
                  </Field>
                  <Field label="Autoplay (ms, slideshow)">
                    <input type="number" className={inputCls} value={currentGallery.autoplay_ms || 0}
                      onBlur={(e) => updateGallery({ autoplay_ms: Number(e.target.value) || 0 })}
                      onChange={(e) => setGalleries((prev) => prev.map((g) => g.id === currentGallery.id ? { ...g, autoplay_ms: Number(e.target.value) } : g))}
                    />
                  </Field>
                  <Field label="Accent color">
                    <input type="color" className="w-full h-10 bg-stone-800 rounded-lg border border-stone-700" value={currentGallery.accent_color || '#d4af37'}
                      onChange={(e) => updateGallery({ accent_color: e.target.value })}
                    />
                  </Field>
                </div>

                <div className="mt-5">
                  <div className="text-sm uppercase tracking-widest text-stone-400 font-semibold mb-3">Default theme — pick a look</div>
                  <ThemePicker
                    value={currentGallery.theme}
                    onChange={(t) => updateGallery({ theme: t })}
                  />
                </div>
                <Field label="Description" className="mt-3">
                  <textarea className={inputCls} rows={2} value={currentGallery.description || ''}
                    onChange={(e) => setGalleries((prev) => prev.map((g) => g.id === currentGallery.id ? { ...g, description: e.target.value } : g))}
                    onBlur={(e) => updateGallery({ description: e.target.value })}
                  />
                </Field>
                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={!!currentGallery.is_public}
                      onChange={(e) => updateGallery({ is_public: e.target.checked })}
                    /> Public
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={!!currentGallery.show_captions}
                      onChange={(e) => updateGallery({ show_captions: e.target.checked })}
                    /> Show captions
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={!!currentGallery.lightbox_enabled}
                      onChange={(e) => updateGallery({ lightbox_enabled: e.target.checked })}
                    /> Lightbox
                  </label>
                </div>
                <div className="mt-3 text-xs text-stone-400">
                  <div className="font-semibold text-stone-300 mb-1">Embed on WordPress</div>
                  <code className="block bg-stone-900 border border-stone-800 rounded p-2 text-[11px] overflow-x-auto">{embedCode}</code>
                </div>
              </section>

              {/* Add media — upload from computer OR import from WordPress */}
              <section className="border border-stone-800 rounded-xl p-5 bg-stone-900/40">
                <h3 className="text-lg font-bold uppercase tracking-widest text-stone-200 mb-4">Add Photos & Videos</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Upload from computer */}
                  <div className="border border-dashed border-stone-700 rounded-xl p-5 bg-stone-900/40 flex flex-col items-center justify-center text-center">
                    <Upload className="w-10 h-10 text-primary mb-3" />
                    <div className="text-base font-bold text-stone-100">Upload from your computer</div>
                    <div className="text-sm text-stone-400 mb-3">Drop images or videos straight from your drive.</div>
                    <label className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-lg text-base font-bold cursor-pointer hover:bg-primary/90">
                      <Upload className="w-5 h-5" />
                      {uploading ? 'Uploading…' : 'Choose files'}
                      <input
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        className="hidden"
                        onChange={handleDirectUpload}
                        data-testid="gc-upload-direct"
                      />
                    </label>
                    {!activeAlbumId && (
                      <div className="mt-3 text-xs text-stone-500">A default album will be created automatically.</div>
                    )}
                  </div>

                  {/* WordPress import */}
                  <div className="border border-dashed border-stone-700 rounded-xl p-5 bg-stone-900/40">
                    <div className="flex items-center gap-2 mb-2">
                      <ExternalLink className="w-5 h-5 text-primary" />
                      <div className="text-base font-bold text-stone-100">Import from WordPress</div>
                    </div>
                    <div className="text-sm text-stone-400 mb-3">Pulls up to 50 images from a WordPress site's REST API.</div>
                    <input
                      className={inputCls}
                      placeholder="https://your-wordpress-site.com"
                      value={importWpUrl}
                      onChange={(e) => setImportWpUrl(e.target.value)}
                      data-testid="gc-wp-url"
                    />
                    <button
                      type="button"
                      onClick={() => setShowWpAuth((s) => !s)}
                      className="mt-2 text-xs text-primary hover:underline"
                    >
                      {showWpAuth ? 'Hide' : 'Private site? Add WordPress login'}
                    </button>
                    {showWpAuth && (
                      <div className="mt-2 space-y-2 bg-stone-900/70 border border-stone-800 rounded-lg p-3">
                        <input
                          className={inputCls}
                          placeholder="WordPress username"
                          value={wpUser}
                          onChange={(e) => setWpUser(e.target.value)}
                          data-testid="gc-wp-user"
                        />
                        <input
                          className={inputCls}
                          type="password"
                          placeholder="Application password (not your login password)"
                          value={wpPass}
                          onChange={(e) => setWpPass(e.target.value)}
                          data-testid="gc-wp-pass"
                        />
                        <div className="text-[11px] text-stone-500">
                          In WordPress, go to <strong>Users → Profile → Application Passwords</strong>, create one, and paste it here. Normal passwords will not work for the REST API.
                        </div>
                      </div>
                    )}
                    <button
                      onClick={importFromWordPress}
                      className="mt-3 w-full px-4 py-3 bg-stone-800 hover:bg-stone-700 rounded-lg text-base font-bold inline-flex items-center justify-center gap-2"
                      data-testid="gc-wp-import"
                    >
                      <FolderPlus className="w-5 h-5" /> Import from WordPress
                    </button>
                  </div>
                </div>
              </section>

              {/* Albums */}
              <section className="border border-stone-800 rounded-xl p-5 bg-stone-900/40">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold uppercase tracking-widest text-stone-200">Albums</h3>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {albums.map((a) => (
                    <button key={a.id} onClick={() => setActiveAlbumId(a.id)}
                      className={`px-4 py-2 rounded-lg text-base font-semibold inline-flex items-center gap-2 ${a.id === activeAlbumId ? 'bg-primary text-primary-foreground' : 'bg-stone-800 text-stone-200 hover:bg-stone-700'}`}
                    >
                      {a.title}
                      <span className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 ${a.id === activeAlbumId ? 'bg-black/20' : 'bg-stone-900 text-stone-300'}`}>
                        <Images className="w-3 h-3" />
                        {a.media_count || 0}
                      </span>
                    </button>
                  ))}
                  {albums.length === 0 && (
                    <div className="text-sm text-stone-500 italic">No albums yet. Add one below ↓</div>
                  )}
                </div>
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <input className={inputCls} placeholder="New album name (e.g. 2024 Mardi Gras)" value={newAlbumTitle} onChange={(e) => setNewAlbumTitle(e.target.value)} />
                  <button onClick={createAlbum} className="px-5 py-3 bg-primary text-primary-foreground rounded-lg text-base font-bold inline-flex items-center gap-2">
                    <FolderPlus className="w-5 h-5" /> Add Album
                  </button>
                </div>
              </section>

              {/* Album editor */}
              {currentAlbum && (
                <section className="border border-stone-800 rounded-xl p-4 bg-stone-900/40">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-300">Album: {currentAlbum.title}</h3>
                    <button onClick={deleteAlbum} className="text-red-300 hover:text-red-400 text-sm inline-flex items-center gap-1">
                      <Trash2 className="w-4 h-4" /> Delete album
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 mb-3">
                    <Field label="Title">
                      <input className={inputCls} value={currentAlbum.title}
                        onChange={(e) => setAlbums((prev) => prev.map((a) => a.id === currentAlbum.id ? { ...a, title: e.target.value } : a))}
                        onBlur={(e) => updateAlbum({ title: e.target.value })}
                      />
                    </Field>
                  </div>
                  <Field label="Description">
                    <textarea className={inputCls} rows={2} value={currentAlbum.description || ''}
                      onChange={(e) => setAlbums((prev) => prev.map((a) => a.id === currentAlbum.id ? { ...a, description: e.target.value } : a))}
                      onBlur={(e) => updateAlbum({ description: e.target.value })}
                    />
                  </Field>
                  <div className="mt-5">
                    <div className="text-sm uppercase tracking-widest text-stone-400 font-semibold mb-3">Album theme (overrides gallery default)</div>
                    <ThemePicker
                      value={currentAlbum.theme || currentGallery.theme}
                      onChange={(t) => updateAlbum({ theme: t })}
                    />
                  </div>

                  <div className="flex items-center justify-between mt-4 mb-2">
                    <span className="text-sm text-stone-300">Media ({media.length})</span>
                    <label className="inline-flex items-center gap-2 px-3 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-sm cursor-pointer">
                      <Upload className="w-4 h-4" />
                      {uploading ? 'Uploading…' : 'Upload Photos / Videos'}
                      <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleUpload} />
                    </label>
                  </div>
                  {media.length === 0 ? (
                    <div className="text-sm text-stone-500 italic">No media. Upload to get started.</div>
                  ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={media.map((m) => m.id)} strategy={rectSortingStrategy}>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                          {media.map((m) => (
                            <MediaTile key={m.id} m={m} onUpdate={updateMedia} onDelete={deleteMedia} />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-4 py-3 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none text-base';
const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-sm uppercase tracking-widest text-stone-400 font-semibold">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

function MediaTile({ m, onUpdate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: m.id });
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(m.title || '');
  const [caption, setCaption] = useState(m.caption || '');
  useEffect(() => { setTitle(m.title || ''); setCaption(m.caption || ''); }, [m.id]);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const save = () => {
    onUpdate(m.id, { title, caption });
    setEditing(false);
  };
  return (
    <div ref={setNodeRef} style={style} className="relative bg-stone-900 border border-stone-800 rounded-xl overflow-hidden group">
      <button {...attributes} {...listeners} className="absolute top-2 left-2 p-1 bg-black/70 rounded z-10 cursor-grab" aria-label="Drag">
        <GripVertical className="w-3 h-3 text-white" />
      </button>
      <button onClick={() => onDelete(m.id)} className="absolute top-2 right-2 p-1 bg-black/70 hover:bg-red-600 rounded z-10">
        <X className="w-3 h-3 text-white" />
      </button>
      {m.media_type === 'video' ? (
        <video src={m.url} className="w-full aspect-square object-cover" />
      ) : (
        <img loading="lazy" decoding="async" src={m.thumb_url || m.url} alt="" className="w-full aspect-square object-cover" />
      )}
      <div className="p-2 space-y-1">
        {editing ? (
          <>
            <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
            <textarea className={inputCls} value={caption} rows={2} onChange={(e) => setCaption(e.target.value)} placeholder="Caption" />
            <div className="flex gap-2">
              <button onClick={save} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 bg-primary text-primary-foreground rounded text-xs"><Save className="w-3 h-3" /> Save</button>
              <button onClick={() => setEditing(false)} className="px-2 py-1 bg-stone-800 rounded text-xs">Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div className="text-xs font-semibold truncate">{m.title || <span className="text-stone-500 italic">No title</span>}</div>
            <div className="text-[11px] text-stone-500 line-clamp-2">{m.caption || '—'}</div>
            <button onClick={() => setEditing(true)} className="w-full mt-1 inline-flex items-center justify-center gap-1 px-2 py-1 bg-stone-800 hover:bg-stone-700 rounded text-xs">
              <Edit2 className="w-3 h-3" /> Edit
            </button>
          </>
        )}
      </div>
    </div>
  );
}
